<p-toast />

<div class="dunning-settings-container">
  <!-- Learn More Popover -->
  <p-popover #learnMorePopover>
    <div class="popover-content learn-more-popover">
      <!-- Sticky Header -->
      <div class="popover-sticky-header">
        <h4>Automate Failed Scheduled Orders with Dunning Workflows</h4>
        <button
          type="button"
          class="popover-close"
          (click)="learnMorePopover.hide()"
          title="Close"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>
      <!-- Scrollable Body -->
      <div class="popover-body">
        <p class="popover-description">
          Dunning Workflows are critical for managing failed Scheduled Orders
          with QPilot. Merchants can create workflows for each failure reason
          (<a
            [href]="dunningDocs.errorCodes"
            target="_blank"
            rel="noopener noreferrer"
            >learn more about error codes here</a
          >) or apply the same workflow to all failures.
        </p>
        <p class="popover-example">
          For example, a dunning workflow for payment errors (2000) might
          involve retry attempts every 6 hours while one for out-of-stock items
          (1001) may retry every 4 days. Merchants can also customize customer
          notifications for dunning retry attempts to ensure high recovery
          rates!
        </p>
        <div class="popover-cta">
          <p>
            <a
              [href]="dunningDocs.workflows"
              target="_blank"
              rel="noopener noreferrer"
              >Learn more about Dunning here</a
            >, or contact our Customer Success team to schedule a personal demo!
          </p>
        </div>
        <p-button
          label="Learn more"
          severity="primary"
          size="small"
          (onClick)="openDocs(dunningDocs.workflows)"
        />
      </div>
    </div>
  </p-popover>

  <!-- Dynamic Fields Popover -->
  <p-popover #dynamicFieldsPopover>
    <div class="popover-content dynamic-fields-popover">
      <!-- Sticky Header -->
      <div class="popover-sticky-header">
        <h4>Dynamic Fields for Dunning Notifications</h4>
        <button
          type="button"
          class="popover-close"
          (click)="dynamicFieldsPopover.hide()"
          title="Close"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>
      <!-- Scrollable Body -->
      <div class="popover-body">
        <p class="popover-intro">
          These dynamic fields can be added for customizing the dunning
          notifications. Dynamic fields can only be used on the corresponding
          dunning notification template.
        </p>
        <div class="dynamic-fields-list">
          <h5>Dunning Notifications</h5>
          @for (field of dynamicFields; track field.variable) {
            <div class="field-item">
              <code>{{ field.variable }}</code>
              <p>{{ field.description }}</p>
            </div>
          }
        </div>
      </div>
    </div>
  </p-popover>

  <!-- Loading State -->
  @if (loading()) {
    <div class="loading-container">
      <!-- Tabs Skeleton -->
      <div class="tabs-container-skeleton">
        <div class="tab-list-skeleton">
          <p-skeleton width="10rem" height="2.5rem" class="tab-skeleton" />
          <p-skeleton width="12rem" height="2.5rem" class="tab-skeleton" />
        </div>
        <div class="tab-content-skeleton">
          <p-skeleton
            width="100%"
            height="4rem"
            borderRadius="8px"
            class="mb-4"
          />
          <p-skeleton
            width="100%"
            height="3rem"
            borderRadius="8px"
            class="mb-4"
          />
          <p-skeleton
            width="100%"
            height="3rem"
            borderRadius="8px"
            class="mb-4"
          />
          <p-skeleton width="100%" height="3rem" borderRadius="8px" />
        </div>
      </div>
      <!-- Actions Skeleton -->
      <div class="actions-container">
        <div class="actions-card">
          <p-skeleton width="150px" height="2.25rem" borderRadius="6px" />
          <p-skeleton width="250px" height="1rem" />
        </div>
      </div>
    </div>
  } @else {
    <!-- Form Content -->
    <form [formGroup]="formGroup" class="form-container">
      <!-- Tabs Container with Learn More button -->
      <div class="tabs-wrapper">
        <!-- Learn More Button - Always visible -->
        <button
          type="button"
          class="tab-level-info-btn"
          (click)="toggleLearnMorePopover($event)"
        >
          <i class="pi pi-info-circle"></i>
          <span>Learn more</span>
        </button>

        <p-tabs
          scrollable
          [value]="activeTabIndex"
          (valueChange)="activeTabIndex = +$event"
        >
          <p-tablist>
            <!-- Retry Attempts Tab -->
            <p-tab [value]="0">
              <i class="pi pi-replay mr-2"></i>
              Retry Attempts
            </p-tab>

            <!-- Notification Templates Tab -->
            <p-tab [value]="1">
              <i class="pi pi-envelope mr-2"></i>
              Notification Templates
            </p-tab>
          </p-tablist>

          <p-tabpanels>
            <!-- Retry Attempts Panel -->
            <p-tabpanel [value]="0">
              <div class="tab-panel-content">
                <p-card styleClass="settings-card">
                  <ng-template pTemplate="title">
                    <div class="card-title">
                      <i class="pi pi-replay"></i>
                      Retry Configuration
                    </div>
                  </ng-template>
                  <ng-template pTemplate="subtitle">
                    Configure how failed Scheduled Orders are automatically
                    retried and what action to take if retries are unsuccessful.
                  </ng-template>
                  <ng-template pTemplate="content">
                    <!-- Enable Toggle -->
                    <div class="form-row toggle-row">
                      <label for="enableDunning" class="toggle-label">
                        Enable Dunning
                      </label>
                      <p-toggleswitch
                        inputId="enableDunning"
                        [formControl]="formGroup.controls.active"
                        (onChange)="onEnableToggle()"
                      />
                    </div>

                    <!-- Two-column grid for error codes and retry config -->
                    <div class="form-grid">
                      <!-- Error Codes Multi-select -->
                      <div class="form-row">
                        <label for="errorCodes" class="field-label">
                          Select Error Codes <span class="required">*</span>
                        </label>
                        <p-multiselect
                          inputId="errorCodes"
                          formControlName="applicableErrorCodes"
                          [options]="errorCodeOptions"
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Select Error Codes"
                          [showClear]="true"
                          display="chip"
                          styleClass="w-full"
                          appendTo="body"
                        />
                        <small class="field-hint"
                          >Error codes to apply settings</small
                        >
                      </div>

                      <!-- Final Action -->
                      <div class="form-row">
                        <label for="dunningAction" class="field-label">
                          Action if retries aren't successful:
                        </label>
                        <p-select
                          inputId="dunningAction"
                          formControlName="dunningAction"
                          [options]="dunningActionOptions"
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Select action"
                          styleClass="w-full"
                          appendTo="body"
                        />
                      </div>
                    </div>

                    <!-- Retry Configuration - Full width -->
                    <div class="form-row retry-config">
                      <p class="config-text">
                        Failed orders will automatically retry every
                      </p>
                      <div class="inline-selects">
                        <p-select
                          formControlName="attemptRetryEveryHours"
                          [options]="retryIntervalOptions"
                          optionLabel="label"
                          optionValue="value"
                          placeholder="interval"
                          styleClass="retry-select"
                          appendTo="body"
                        />
                        <span class="config-text">up to</span>
                        <p-select
                          formControlName="retryForInAttempts"
                          [options]="retryAttemptOptions"
                          optionLabel="label"
                          optionValue="value"
                          placeholder="times"
                          styleClass="retry-select"
                          appendTo="body"
                        />
                      </div>
                    </div>
                  </ng-template>
                </p-card>
              </div>
            </p-tabpanel>

            <!-- Notification Templates Panel -->
            <p-tabpanel [value]="1">
              <div class="tab-panel-content">
                <p-card styleClass="settings-card">
                  <ng-template pTemplate="title">
                    <div class="card-title-with-action">
                      <div class="card-title">
                        <i class="pi pi-envelope"></i>
                        Email Notifications
                      </div>
                      <button
                        type="button"
                        class="dynamic-fields-btn"
                        (click)="toggleDynamicFieldsPopover($event)"
                      >
                        <i class="pi pi-question-circle"></i>
                        <span>What dynamic fields can I use?</span>
                      </button>
                    </div>
                  </ng-template>
                  <ng-template pTemplate="subtitle">
                    Customize the notification messages sent to customers during
                    the dunning process. Use dynamic fields to personalize
                    messages.
                  </ng-template>
                  <ng-template pTemplate="content">
                    <!-- Single column grid for notification fields -->
                    <div class="form-grid form-grid--notifications">
                      <!-- First Retry -->
                      <div class="form-row">
                        <label for="firstRetry" class="field-label">
                          First Retry Attempt
                        </label>
                        <input
                          pInputText
                          id="firstRetry"
                          formControlName="firstRetry"
                          class="w-full"
                          placeholder="Notification for first retry"
                        />
                        @if (
                          formGroup.controls.firstRetry.errors?.[
                            "whitespaceOnly"
                          ]
                        ) {
                          <small class="field-error"
                            >This field cannot be empty</small
                          >
                        }
                      </div>

                      <!-- Retry Attempts -->
                      <div class="form-row">
                        <label for="retryAttempts" class="field-label">
                          Retry Attempts
                        </label>
                        <input
                          pInputText
                          id="retryAttempts"
                          formControlName="retryAttempts"
                          class="w-full"
                          placeholder="Notification for retries"
                        />
                        @if (
                          formGroup.controls.retryAttempts.errors?.[
                            "whitespaceOnly"
                          ]
                        ) {
                          <small class="field-error"
                            >This field cannot be empty</small
                          >
                        }
                      </div>

                      <!-- Final Retry -->
                      <div class="form-row">
                        <label for="lastRetry" class="field-label">
                          Final Retry Attempt
                        </label>
                        <input
                          pInputText
                          id="lastRetry"
                          formControlName="lastRetry"
                          class="w-full"
                          placeholder="Notification for final retry"
                        />
                        @if (
                          formGroup.controls.lastRetry.errors?.[
                            "whitespaceOnly"
                          ]
                        ) {
                          <small class="field-error"
                            >This field cannot be empty</small
                          >
                        }
                      </div>

                      <!-- Action Applied -->
                      <div class="form-row">
                        <label for="finished" class="field-label">
                          Action Applied
                        </label>
                        <input
                          pInputText
                          id="finished"
                          formControlName="finished"
                          class="w-full"
                          placeholder="Notification when action applied"
                        />
                        @if (
                          formGroup.controls.finished.errors?.["whitespaceOnly"]
                        ) {
                          <small class="field-error"
                            >This field cannot be empty</small
                          >
                        }
                      </div>
                    </div>
                  </ng-template>
                </p-card>
              </div>
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      </div>

      <!-- Sticky Bottom Actions - Button on left -->
      <div class="actions-container">
        <div class="actions-card">
          <div class="actions-buttons">
            <p-button
              label="Update Settings"
              icon="pi pi-save"
              [loading]="saving()"
              [disabled]="!canSave"
              (onClick)="saveSettings()"
              size="small"
            />
          </div>
          @if (isFormDirty) {
            @if (!formGroup.controls.active.value) {
              <div class="changes-indicator changes-indicator--warning">
                <i class="pi pi-exclamation-triangle"></i>
                <span>Dunning will be disabled</span>
              </div>
            } @else {
              <div class="changes-indicator">
                <i class="pi pi-info-circle"></i>
                <span>Changes will be applied immediately</span>
              </div>
            }
          }
        </div>
      </div>
    </form>
  }

  <!-- Upgrade Dialog -->
  <p-dialog
    header="Upgrade Required"
    [modal]="true"
    [visible]="showUpgradeDialog()"
    [closable]="true"
    [style]="{ width: '450px' }"
    (onHide)="closeUpgradeDialog()"
  >
    <div class="upgrade-dialog-content">
      <i class="pi pi-exclamation-triangle warning-icon"></i>
      <p>
        Your <strong>{{ currentTierName || "current plan" }}</strong> does not
        allow the activation of Dunning.
      </p>
      <p>To enable Dunning, please upgrade your subscription.</p>
    </div>
    <ng-template pTemplate="footer">
      <p-button
        label="Cancel"
        severity="secondary"
        [text]="true"
        (onClick)="closeUpgradeDialog()"
      />
      <p-button
        label="UPGRADE NOW"
        severity="primary"
        (onClick)="goToUpgrade()"
      />
    </ng-template>
  </p-dialog>
</div>
